/**
 * LandingScreen  -  cinematic dark-ambient landing for unauthenticated users.
 *
 * Three-phase intro:
 *   'intro'  (0–2.8 s) : pure black canvas, domain-warped FBM fabric pattern at center,
 *                        "Welcome." fades in slowly.
 *   'reveal' (2.8–4.6s): text drifts up/away, fabric expands to fill viewport.
 *   'main'   (4.6 s +) : full-screen dark organic silk fabric, stackbtc wordmark, auth form.
 *
 * Aesthetic: "sophisticated dark  -  organic shifting gradients that give the impression
 * of fluid sculpted surfaces. UI elements minimal, translucent, floating."
 *
 * WebGL shader: domain-warped FBM (two warp layers → folded-silk fabric). Cursor applies
 * a very wide, very subtle perpendicular push to the UV field. Site palette: dark near-black
 * blue-violet + dark reddish-purple. ~24fps quantized film grain throughout.
 *
 * Skip-intro: sessionStorage 'stackbtc-intro-v3' flag; returning users skip to 'main'.
 */

import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { PROSPECT } from '../prospect.js';
import { getCurrentSubdomain, isFIRETenant } from '../io/tenant.js';

// Password gate: bradlong.stackbtc.net requires an access code before the
// email/OTP flow. Stored as a module constant so it is evaluated once.
// The value is in the compiled bundle  -  acceptable here because the intent
// is a lightweight invitation gate, not cryptographic security.
const _sub = getCurrentSubdomain();
const SITE_PASSWORD: string | null =
  _sub === 'bradlong' || _sub === 'trey' || _sub === 'tim' ? '1605'
  : _sub === 'private' || _sub === 'paul' ? '16052003'
  : null;

// ── WebGL shader sources ───────────────────────────────────────────────────

const VERT_SRC = `
attribute vec2 a_pos;
void main() {
  gl_Position = vec4(a_pos, 0.0, 1.0);
}
`;

const PALETTE = _sub === 'tim'
  ? `  vec3 c0 = vec3(0.003, 0.002, 0.001);
  vec3 c1 = vec3(0.018, 0.013, 0.004);
  vec3 c2 = vec3(0.050, 0.038, 0.010);
  vec3 c3 = vec3(0.115, 0.090, 0.022);
  vec3 c4 = vec3(0.200, 0.155, 0.040);
  vec3 ch = vec3(0.180, 0.140, 0.035);`
  : _sub === 'private' || _sub === 'paul'
  ? `  vec3 c0 = vec3(0.001, 0.008, 0.002);
  vec3 c1 = vec3(0.003, 0.028, 0.006);
  vec3 c2 = vec3(0.005, 0.065, 0.014);
  vec3 c3 = vec3(0.008, 0.108, 0.022);
  vec3 c4 = vec3(0.009, 0.142, 0.028);
  vec3 ch = vec3(0.008, 0.128, 0.025);`
  : `  vec3 c0 = vec3(0.001, 0.007, 0.020);
  vec3 c1 = vec3(0.002, 0.028, 0.068);
  vec3 c2 = vec3(0.004, 0.062, 0.140);
  vec3 c3 = vec3(0.007, 0.102, 0.210);
  vec3 c4 = vec3(0.008, 0.128, 0.255);
  vec3 ch = vec3(0.007, 0.115, 0.238);`;

// Silk-beam shader  -  models light reflecting off a large curved surface.
//
// Key design decisions:
//   - Only 3 FBM octaves (not 5) so features stay large and smooth, not dripping.
//   - Very low spatial frequency (st * 0.30) so each feature spans the whole screen.
//   - Single warp layer (q only, not q→r) for one clean bend, not fractal tangles.
//   - Color mapped as a single gradient (dark → bright cobalt/blue → blue-violet)
//     mimicking the "specular highlight on silk" look from the reference.
//   - Wide smoothstep ranges throughout for dreamy, edge-free transitions.
const FRAG_SRC = `
precision highp float;
uniform float u_time;
uniform vec2  u_res;
uniform float u_expand;
uniform vec2  u_mouse;
uniform vec2  u_vel;
uniform float u_gamma;

vec2 gh(vec2 p) {
  p = vec2(dot(p, vec2(127.1, 311.7)), dot(p, vec2(269.5, 183.3)));
  return fract(sin(p) * 43758.5453) * 2.0 - 1.0;
}
float gn(vec2 p) {
  vec2 i = floor(p), f = fract(p), u = f*f*(3.0-2.0*f);
  return mix(
    mix(dot(gh(i),           f           ), dot(gh(i+vec2(1,0)), f-vec2(1,0)), u.x),
    mix(dot(gh(i+vec2(0,1)), f-vec2(0,1) ), dot(gh(i+vec2(1,1)), f-vec2(1,1)), u.x),
    u.y
  );
}
// 3 octaves only  -  keeps features large, smooth, silk-like, not dripping
float fbm(vec2 p) {
  float v=0.0, a=0.60;
  mat2 R = mat2(1.6, 1.1, -1.1, 1.6);
  v += a*gn(p); p=R*p; a*=0.50;
  v += a*gn(p); p=R*p; a*=0.50;
  v += a*gn(p);
  return v;
}
float hash1(vec2 p) {
  return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
}

void main() {
  vec2 st = (gl_FragCoord.xy * 2.0 - u_res) / min(u_res.x, u_res.y);
  float dist = length(st);

  // Cursor: velocity-based local push. Gaussian falloff from cursor position so
  // the effect is wide but not global. Push direction = cursor velocity (not
  // position), so stationary cursor does nothing  -  no focal point or attractor.
  // Negated: positive velocity pushes the UV field forward (light moves WITH cursor).
  vec2 mPos   = u_mouse * u_res / min(u_res.x, u_res.y);
  vec2 mDelta = st - mPos;
  float mDist = length(mDelta);
  // k=0.42: ~72% push at 1/4 screen, ~27% at 1/2 screen  -  local but wide
  float mFall = exp(-mDist * mDist * 0.42);
  vec2 cursorW = -u_vel * mFall * 0.014;

  // Sinusoidal drift  -  two different frequencies each axis so motion is
  // omnidirectional and floaty, not a steady fall in one direction
  // ALL time offsets are sinusoidal  -  nothing linear so no net directional drift.
  // Different frequencies + phases per axis make the motion omnidirectional.
  float s1x = sin(u_time * 0.031 + 0.00) * 0.90;
  float s1y = cos(u_time * 0.024 + 1.20) * 0.85;
  float s2x = sin(u_time * 0.019 + 2.10) * 0.75;
  float s2y = cos(u_time * 0.027 + 3.40) * 0.80;

  // Slow Lissajous-style ambient drift  -  never repeats exactly, no dominant axis
  vec2 drift = vec2(
    sin(u_time * 0.055 + 0.80) * 0.022 + sin(u_time * 0.029 + 2.10) * 0.013,
    cos(u_time * 0.041 + 1.50) * 0.019 + cos(u_time * 0.037 + 0.40) * 0.011
  );

  // LOW frequency  -  0.30 means one FBM feature spans the full screen width
  vec2 p = st * 0.30 + cursorW;

  // Single warp layer  -  both warp samples oscillate in X AND Y, no falling
  vec2 q = vec2(
    fbm(p + vec2(s1x, s1y) + drift),
    fbm(p + vec2(5.20 + s2x, s2y) + drift)
  );
  float f = fbm(p + 4.5*q + vec2(1.7, 9.2));

  // S-curve remap  -  ultra-smooth, no kinks
  f = f * 0.5 + 0.5;
  f = clamp(f, 0.0, 1.0);
  f = f * f * (3.0 - 2.0 * f);
  // Second pass S-curve softens further  -  makes beam edges truly gradient
  f = f * f * (3.0 - 2.0 * f);
  // Dark bias: pulls mid-range values toward black without touching the bright peaks.
  // pow < 1 would brighten; pow > 1 deepens the dark folds between beams.
  f = pow(f, u_gamma);

  // Silk-beam color gradient: near-black → deep navy → cobalt/indigo → blue-violet.
  // Predominantly blue with a noticeable purple tinge toward the peaks.
  ${PALETTE}

  // Very wide transitions  -  no visible edge anywhere
  vec3 col = c0;
  col = mix(col, c1, smoothstep(0.08, 0.46, f));
  col = mix(col, c2, smoothstep(0.25, 0.64, f));
  col = mix(col, c3, smoothstep(0.45, 0.78, f));
  col = mix(col, c4, smoothstep(0.62, 0.91, f) * 0.75);
  col = mix(col, ch, smoothstep(0.80, 1.00, f) * 0.55);

  // Blob mask: soft gradient edge that grows wider and more transparent as the
  // circle expands. blobR pushed slightly outward so the gradient doesn't eat
  // into the bright center. blobSoft starts generous (0.42) for a visible
  // gradient even in the small intro bubble, then grows to 1.50 at full screen.
  float intr    = 1.0 - u_expand;
  float breathe = sin(u_time * 0.42) * 0.055 * intr;
  float blobR    = 0.86 + u_expand * 3.30 + breathe;
  float blobSoft = 0.42 + u_expand * 1.08;
  float rawMask  = smoothstep(blobR, blobR - blobSoft, dist);
  // Power rises with expansion: makes the outer gradient zone progressively more
  // transparent as the circle opens  -  a gentle feather that avoids any hard rim.
  col *= pow(rawMask, 1.0 + u_expand * 0.85);

  // Fine film grain  -  per pixel, not blocked
  float grainT = floor(u_time * 24.0);
  float g = hash1(gl_FragCoord.xy + vec2(grainT * 7.31, grainT * 5.17));
  col = clamp(col + g * 0.028 - 0.014, 0.0, 1.0);

  gl_FragColor = vec4(col, 1.0);
}
`;

// ── WebGL init ─────────────────────────────────────────────────────────────

interface GLCtx {
  gl: WebGLRenderingContext;
  prog: WebGLProgram;
  uTime: WebGLUniformLocation;
  uRes: WebGLUniformLocation;
  uExpand: WebGLUniformLocation;
  uMouse: WebGLUniformLocation | null;
  uVel: WebGLUniformLocation | null;
  uGamma: WebGLUniformLocation | null;
}

function initWebGL(canvas: HTMLCanvasElement): GLCtx | null {
  const gl = canvas.getContext('webgl', { antialias: false, alpha: false, depth: false, preserveDrawingBuffer: true });
  if (!gl) return null;

  function compile(type: number, src: string): WebGLShader | null {
    const s = gl!.createShader(type);
    if (!s) return null;
    gl!.shaderSource(s, src);
    gl!.compileShader(s);
    if (!gl!.getShaderParameter(s, gl!.COMPILE_STATUS)) {
      console.error('Shader error:', gl!.getShaderInfoLog(s));
      return null;
    }
    return s;
  }

  const vert = compile(gl.VERTEX_SHADER, VERT_SRC);
  const frag = compile(gl.FRAGMENT_SHADER, FRAG_SRC);
  if (!vert || !frag) return null;

  const prog = gl.createProgram()!;
  gl.attachShader(prog, vert);
  gl.attachShader(prog, frag);
  gl.linkProgram(prog);
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
    console.error('Link error:', gl.getProgramInfoLog(prog));
    return null;
  }
  gl.useProgram(prog);
  gl.deleteShader(vert);
  gl.deleteShader(frag);

  const buf = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1, 1,-1, -1,1, 1,1]), gl.STATIC_DRAW);
  const aPos = gl.getAttribLocation(prog, 'a_pos');
  gl.enableVertexAttribArray(aPos);
  gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

  return {
    gl, prog,
    uTime:   gl.getUniformLocation(prog, 'u_time')!,
    uRes:    gl.getUniformLocation(prog, 'u_res')!,
    uExpand: gl.getUniformLocation(prog, 'u_expand')!,
    uMouse:  gl.getUniformLocation(prog, 'u_mouse'),
    uVel:    gl.getUniformLocation(prog, 'u_vel'),
    uGamma:  gl.getUniformLocation(prog, 'u_gamma'),
  };
}

// ── Component ─────────────────────────────────────────────────────────────

type Phase = 'intro' | 'reveal' | 'main';

export interface LandingScreenProps {
  /** Animation-only mode: skip the auth form and call onRevealComplete when done. */
  autoReveal?: boolean;
  onRevealComplete?: () => void;
  /** Called when the welcome animation finishes and the auth form appears (non-autoReveal). */
  onWelcomeComplete?: () => void;
  status?: 'loading' | 'signed-out' | 'sending' | 'sent' | 'verifying';
  error?: string | null;
  pendingEmail?: string | null;
  onSubmitEmail?: (email: string) => void;
  onSubmitCode?: (code: string) => void;
  onReset?: () => void;
}

export function LandingScreen({
  autoReveal = false, onRevealComplete, onWelcomeComplete,
  status, error, pendingEmail, onSubmitEmail, onSubmitCode, onReset,
}: LandingScreenProps): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef    = useRef<number>(0);
  const t0Ref     = useRef(performance.now());

  // Expand is animated in the render loop (no re-renders)
  const expandRef      = useRef(0);
  const revealStartRef = useRef<number | null>(null);
  const REVEAL_DUR_S   = 1.0;

  // Smoothed mouse  -  target updates instantly, actual position lerps per frame.
  // The lag (tx - x) is the velocity signal: bounded by screen size, decays naturally.
  // init flag: snap smoothed pos to target on first move to avoid opening-jerk spike.
  const mouseRef = useRef({ x: 0, y: 0, tx: 0, ty: 0, init: false });

  // Phase / text visibility
  const [phase,         setPhase        ] = useState<Phase>('intro');
  const [introBlack,    setIntroBlack   ] = useState(true);   // solid cover until first rAF
  const [showWelcome,   setShowWelcome  ] = useState(false);
  const [exitWelcome,   setExitWelcome  ] = useState(false);
  const [titleVisible,  setTitleVisible ] = useState(false);
  const [titleFloating, setTitleFloating] = useState(false);
  const [cardVisible,   setCardVisible  ] = useState(false);

  // Typewriter for the wordmark  -  always "stackbtc" regardless of prospect
  // branding. The prospect wordmark lives in the tagline / document title.
  const WORDMARK = 'stackbtc';
  const [typedCount,    setTypedCount   ] = useState(0);
  const [cursorVisible, setCursorVisible] = useState(false);

  // Blink the cursor from mount
  useEffect(() => {
    const t = setInterval(() => setCursorVisible(v => !v), 530);
    return () => clearInterval(t);
  }, []);

  // Type out the wordmark once titleVisible flips
  useEffect(() => {
    if (!titleVisible) return;
    let i = 0;
    const t = setInterval(() => {
      i += 1;
      setTypedCount(i);
      if (i >= WORDMARK.length) clearInterval(t);
    }, 90);
    return () => clearInterval(t);
  }, [titleVisible]);

  // Password gate state (bradlong only). Persisted in sessionStorage so
  // the user doesn't re-enter on hard refresh within the same session.
  const [passwordPassed, setPasswordPassed] = useState<boolean>(() => {
    if (!SITE_PASSWORD) return true;
    try { return window.sessionStorage.getItem('stackbtc-access-v1') === '1'; } catch { return false; }
  });
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const handlePasswordSubmit = useCallback((e: FormEvent): void => {
    e.preventDefault();
    if (passwordInput === SITE_PASSWORD) {
      try { window.sessionStorage.setItem('stackbtc-access-v1', '1'); } catch { /* quota/private mode */ }
      setPasswordPassed(true);
      setPasswordError(null);
    } else {
      setPasswordError('Incorrect access code. Please try again.');
    }
  }, [passwordInput]);

  // Form state
  const [email,   setEmail  ] = useState('');
  const [otpCode, setOtpCode] = useState('');

  // ── WebGL render loop ────────────────────────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = initWebGL(canvas);

    function resize() {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas!.width  = Math.round(window.innerWidth  * dpr);
      canvas!.height = Math.round(window.innerHeight * dpr);
      if (ctx) ctx.gl.viewport(0, 0, canvas!.width, canvas!.height);
    }
    resize();
    window.addEventListener('resize', resize);

    function onMouseMove(e: MouseEvent) {
      const m = mouseRef.current;
      const nx =  (e.clientX / window.innerWidth  - 0.5) * 2;
      const ny = -(e.clientY / window.innerHeight - 0.5) * 2;
      if (!m.init) { m.x = nx; m.y = ny; m.init = true; }
      m.tx = nx; m.ty = ny;
    }
    function onTouchMove(e: TouchEvent) {
      const touch = e.touches[0];
      if (!touch) return;
      const m = mouseRef.current;
      const nx =  (touch.clientX / window.innerWidth  - 0.5) * 2;
      const ny = -(touch.clientY / window.innerHeight - 0.5) * 2;
      if (!m.init) { m.x = nx; m.y = ny; m.init = true; }
      m.tx = nx; m.ty = ny;
    }
    window.addEventListener('mousemove', onMouseMove, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: true });

    function render() {
      const now = performance.now();
      const t   = (now - t0Ref.current) / 1000;

      // Power-curve expand: pow(t, 0.52) starts fast immediately (no slow ramp-up
      // like smoothstep) and keeps accelerating the visible area since blob area ∝ r².
      if (revealStartRef.current !== null) {
        const tN = Math.min(1, (now - revealStartRef.current) / 1000 / REVEAL_DUR_S);
        expandRef.current = Math.pow(tN, 0.52);
      }

      // Slow lerp so mouse lags visibly  -  the lag (tx-x, ty-y) IS the velocity signal.
      // This is naturally bounded (can't exceed screen size) and decays automatically
      // when the cursor stops, so no accumulation spikes from rapid mousemove events.
      const m = mouseRef.current;
      m.x += (m.tx - m.x) * 0.028;
      m.y += (m.ty - m.y) * 0.028;

      if (ctx) {
        const { gl, uTime, uRes, uExpand, uMouse, uVel, uGamma } = ctx;
        gl.uniform1f(uTime,   t);
        gl.uniform2f(uRes,    canvas!.width, canvas!.height);
        gl.uniform1f(uExpand, expandRef.current);
        if (uMouse) gl.uniform2f(uMouse, m.x, m.y);
        if (uVel)   gl.uniform2f(uVel, m.tx - m.x, m.ty - m.y);
        // Portrait screens show a taller FBM slice with more dark regions  -  reduce
        // the dark bias proportionally so the pattern stays visible on mobile.
        if (uGamma) {
          const aspect = canvas!.height / canvas!.width;
          const gamma  = aspect > 1 ? Math.max(1.10, 1.40 / aspect) : 1.40;
          gl.uniform1f(uGamma, gamma);
        }
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      }

      rafRef.current = requestAnimationFrame(render);
    }
    render();

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('touchmove', onTouchMove);
      if (ctx) ctx.gl.deleteProgram(ctx.prog);
    };
  }, []);

  // ── Phase state machine  -  always plays full intro on every page load ────────
  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];

    // setTimeout instead of double-rAF: iOS Safari suspends rAF in power-efficiency
    // mode on first page load (same class of bug as the cardVisible fix in b10c8b0).
    // If the rAF never fires, introBlack stays true and the pure-black overlay
    // sits on top of the WebGL canvas forever  -  user sees a black screen and must
    // refresh. 32ms (~2 frames) guarantees the initial paint has occurred without
    // relying on rAF scheduling.
    timers.push(setTimeout(() => {
      setIntroBlack(false);   // black overlay begins fading (0.90s)
      setShowWelcome(true);   // welcome begins fading in behind overlay
    }, 32));

    // t=1400ms  -  welcome has been visible ~500ms; drift up + circle expands
    timers.push(setTimeout(() => {
      setShowWelcome(false);
      setExitWelcome(true);
      revealStartRef.current = performance.now();
      setPhase('reveal');
    }, 1400));

    // t=2600ms  -  circle has filled screen; reveal complete.
    // Previously used a double-rAF + setTimeout chain here, but iOS Safari
    // suspends rAF callbacks in power-efficiency mode after a few seconds,
    // which left cardVisible=false forever (stuck on silk background). Plain
    // setTimeouts are reliable across all mobile browsers.
    timers.push(setTimeout(() => {
      setPhase('main');
      if (autoReveal) {
        onRevealComplete?.();
      } else {
        onWelcomeComplete?.();
        setTitleVisible(true);
        timers.push(setTimeout(() => setCardVisible(true), 500));
        timers.push(setTimeout(() => setTitleFloating(true), 2000));
      }
    }, 2600));

    return () => timers.forEach(clearTimeout);
  }, []);

  const handleEmailSubmit = useCallback((e: FormEvent) => {
    e.preventDefault();
    onSubmitEmail?.(email.trim());
  }, [email, onSubmitEmail]);

  const handleCodeSubmit = useCallback((e: FormEvent) => {
    e.preventDefault();
    onSubmitCode?.(otpCode);
  }, [otpCode, onSubmitCode]);

  const isCodePhase = status === 'sent' || status === 'verifying';

  return (
    <div style={{ position: 'fixed', inset: 0, overflow: 'hidden', background: '#000000', zIndex: 100 }}>

      {/* ── WebGL canvas ── */}
      <canvas
        ref={canvasRef}
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', display: 'block' }}
      />

      {/* ── Subtle vignette  -  deepens the very edges slightly ── */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute', inset: 0,
          background: 'radial-gradient(ellipse 88% 88% at 50% 50%, transparent 35%, rgba(0,0,0,0.52) 100%)',
          pointerEvents: 'none',
          zIndex: 1,
        }}
      />

      {/* ── "Welcome."  -  intro + reveal phases ── */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute', inset: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          pointerEvents: 'none',
          zIndex: 4,
          opacity: phase === 'main' ? 0 : 1,
          transition: phase === 'main' ? 'opacity 0.5s ease' : 'none',
        }}
      >
        <span style={{
          fontFamily: 'var(--sans)',
          fontSize: 'clamp(1.4rem, 3.2vw, 2.2rem)',
          fontWeight: 300,
          letterSpacing: '0.04em',
          color: 'rgba(255,255,255,0.88)',
          opacity: showWelcome ? 1 : 0,
          transform: exitWelcome
            ? 'translateY(-72px)'
            : showWelcome ? 'translateY(0px)' : 'translateY(10px)',
          transition: showWelcome
            ? 'opacity 0.85s cubic-bezier(0.2,0.8,0.2,1), transform 0.85s cubic-bezier(0.2,0.8,0.2,1)'
            : exitWelcome
              ? 'opacity 1.08s ease, transform 1.20s cubic-bezier(0.4,0,0.2,1)'
              : 'none',
          userSelect: 'none',
        }}>
          Welcome
        </span>
      </div>

      {/* ── Main UI  -  stackbtc wordmark + auth card (hidden in autoReveal mode) ── */}
      {!autoReveal && (<div style={{
        position: 'absolute', inset: 0,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        padding: '2rem',
        zIndex: 5,
        pointerEvents: phase === 'main' ? 'auto' : 'none',
      }}>

        {/* Title: typewriter reveal, same font as "Welcome", float after typing done. */}
        <div style={{
          opacity: titleVisible ? 1 : 0,
          marginBottom: '2.2rem',
          textAlign: 'center',
          position: 'relative',
          width: '100%',
          maxWidth: 400,
        }}>
          {/* Blur glow  -  always shows full word so it glows behind the cursor too */}
          <div aria-hidden="true" style={{
            position: 'absolute',
            top: '-0.4rem', bottom: '-0.4rem',
            left: '-0.8rem', right: '-0.8rem',
            fontFamily: 'var(--sans)',
            fontWeight: 300,
            fontSize: 'clamp(3.2rem, 8vw, 7rem)',
            letterSpacing: '0.04em',
            color: 'rgba(130, 155, 255, 0.32)',
            filter: 'blur(28px)',
            userSelect: 'none',
            pointerEvents: 'none',
            lineHeight: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: typedCount >= WORDMARK.length ? 1 : 0,
            transition: 'opacity 0.8s ease',
          }}>{WORDMARK}</div>
          <div
            className={titleFloating ? 'ls-title-alive' : ''}
            style={{
              position: 'relative',
              fontFamily: 'var(--sans)',
              fontWeight: 300,
              fontSize: 'clamp(3.2rem, 8vw, 7rem)',
              letterSpacing: '0.04em',
              color: '#ffffff',
              userSelect: 'none',
              lineHeight: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {WORDMARK.slice(0, typedCount)}
            {typedCount < WORDMARK.length && (
              <span style={{
                color: 'rgba(255,255,255,0.70)',
                marginLeft: '0.02em',
                lineHeight: 1,
                opacity: cursorVisible ? 1 : 0,
                transition: 'opacity 0.07s',
              }}>|</span>
            )}
          </div>
        </div>

        {/* Auth glass card */}
        <div style={{
          width: '100%',
          maxWidth: 400,
          background: 'rgba(4, 2, 10, 0.75)',
          backdropFilter: 'blur(30px)',
          WebkitBackdropFilter: 'blur(30px)',
          borderRadius: 12,
          border: '1px solid rgba(255,255,255,0.07)',
          padding: '1.75rem 2rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '1.1rem',
          opacity: cardVisible ? 1 : 0,
          transform: cardVisible ? 'translateY(0px)' : 'translateY(18px)',
          transition: 'opacity 1.4s cubic-bezier(0.2,0.8,0.2,1), transform 1.4s cubic-bezier(0.2,0.8,0.2,1)',
        }}>

          {!passwordPassed ? (
            /* ── Password gate (bradlong only) ── */
            <>
              <p style={{ fontSize: 'var(--fs-small)', color: 'var(--text-soft)', lineHeight: 1.65, margin: 0 }}>
                Stack Bitcoin and model your path to financial independence. Access by invitation only. Enter your code to continue.
              </p>
              <form onSubmit={handlePasswordSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
                <input
                  className="ls-input"
                  type="password"
                  autoComplete="current-password"
                  value={passwordInput}
                  onChange={e => { setPasswordInput(e.target.value); setPasswordError(null); }}
                  placeholder="Access code"
                  autoFocus
                  required
                  style={{
                    fontSize: 'var(--fs-body)',
                    padding: '10px 14px',
                    background: 'rgba(0,0,0,0.50)',
                    border: `1px solid ${passwordError ? 'var(--neg)' : 'rgba(255,255,255,0.09)'}`,
                    borderRadius: 8,
                    color: '#fff',
                    outline: 'none',
                    width: '100%',
                    boxSizing: 'border-box',
                    transition: 'border-color 0.2s, box-shadow 0.2s',
                  }}
                />
                {passwordError ? <div style={{ fontSize: 'var(--fs-micro)', color: 'var(--neg)' }}>{passwordError}</div> : null}
                <button
                  type="submit"
                  className="primary"
                  disabled={!passwordInput}
                  style={{ alignSelf: 'flex-start', padding: '8px 20px' }}
                >
                  Continue
                </button>
              </form>
            </>
          ) : isCodePhase ? (
            <>
              <p style={{ fontSize: 'var(--fs-small)', color: 'var(--text-soft)', lineHeight: 1.65, margin: 0 }}>
                We sent a code to{' '}
                <strong style={{ color: 'rgba(255,255,255,0.88)', fontWeight: 500 }}>
                  {pendingEmail}
                </strong>
                . Enter it below.
              </p>

              <form onSubmit={handleCodeSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
                <input
                  className="ls-input"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]*"
                  maxLength={10}
                  value={otpCode}
                  onChange={e => setOtpCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="••••••••"
                  autoFocus
                  required
                  style={{
                    fontSize: '1.9rem',
                    padding: '12px 16px',
                    letterSpacing: '0.5em',
                    textAlign: 'center',
                    fontFamily: 'var(--mono)',
                    background: 'rgba(0,0,0,0.50)',
                    border: '1px solid rgba(255,255,255,0.09)',
                    borderRadius: 8,
                    color: '#fff',
                    outline: 'none',
                    width: '100%',
                    boxSizing: 'border-box',
                    transition: 'border-color 0.2s, box-shadow 0.2s',
                  }}
                />
                {error ? <div style={{ fontSize: 'var(--fs-micro)', color: 'var(--neg)' }}>{error}</div> : null}
                <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
                  <button
                    type="submit"
                    className="primary"
                    disabled={status === 'verifying' || otpCode.length < 6}
                    style={{ padding: '8px 20px' }}
                  >
                    {status === 'verifying' ? 'Verifying…' : 'Verify code'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setOtpCode(''); onReset?.(); }}
                    style={{ background: 'none', border: 'none', fontSize: 'var(--fs-small)', color: 'var(--text-faint)', cursor: 'pointer', padding: '4px 0' }}
                  >
                    Use a different email
                  </button>
                </div>
              </form>
            </>
          ) : (
            <>
              <p style={{ fontSize: 'var(--fs-small)', color: 'var(--text-soft)', lineHeight: 1.65, margin: 0 }}>
                {'We\'ll email you a one-time code. Your data syncs across every device you sign in with.'}
              </p>

              <form onSubmit={handleEmailSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
                <input
                  className="ls-input"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  autoFocus
                  required
                  style={{
                    fontSize: 'var(--fs-body)',
                    padding: '10px 14px',
                    background: 'rgba(0,0,0,0.50)',
                    border: '1px solid rgba(255,255,255,0.09)',
                    borderRadius: 8,
                    color: '#fff',
                    outline: 'none',
                    width: '100%',
                    boxSizing: 'border-box',
                    transition: 'border-color 0.2s, box-shadow 0.2s',
                  }}
                />
                {error ? <div style={{ fontSize: 'var(--fs-micro)', color: 'var(--neg)' }}>{error}</div> : null}
                <button
                  type="submit"
                  className="primary"
                  disabled={status === 'sending' || !email.includes('@')}
                  style={{ alignSelf: 'flex-start', padding: '8px 20px' }}
                >
                  {status === 'sending' ? 'Sending…' : 'Send code'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>)}

      {/* ── Black intro cover  -  fades out after first rAF, revealing circle + welcome ── */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute', inset: 0,
          background: '#000',
          opacity: introBlack ? 1 : 0,
          transition: introBlack ? 'none' : 'opacity 0.90s ease',
          pointerEvents: 'none',
          zIndex: 20,
        }}
      />
    </div>
  );
}
